import { Popover } from '@mui/material';
import { InlineDatePicker, Period } from 'components/Form/InlineDatePicker/InlineDatePicker';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import {
    formatWaitlistDates,
    getWaitlistBoardingNights,
    WAITLIST_DATE_FORMAT,
    WAITLIST_SINGLE_DATE_FORMAT
} from 'utils/waitlistUtils';
import { WaitlistPickerField } from './WaitlistPickerField';
import classes from './WaitlistDatesField.module.scss';

interface WaitlistDatesFieldProps {
    label: string;
    businessArea?: MarketplaceServiceCategory;
    /** "YYYY-MM-DD", one per occurrence; boarding keeps the nights. */
    dates: Array<string>;
    timeZone: string;
    minDate: Moment;
    maxDate: Moment;
    isDateDisabled?: (date: Date) => boolean;
    getDisabledDateTooltip?: (date: Date) => string | undefined;
    onChange: (dates: Array<string>) => void;
}

/**
 * Requested dates of a waitlist request. The calendar follows the business area, the same
 * way the date step of each area does: one day for grooming, several for daycare and a
 * check-in / check-out range for boarding.
 */
export const WaitlistDatesField: React.FC<WaitlistDatesFieldProps> = ({
    label,
    businessArea,
    dates,
    timeZone,
    minDate,
    maxDate,
    isDateDisabled,
    getDisabledDateTooltip,
    onChange
}) => {
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const [isOpen, setIsOpen] = React.useState(false);

    const isBoarding = businessArea === MarketplaceServiceCategory.BOARDING;
    const isDaycare = businessArea === MarketplaceServiceCategory.DAYCARE;

    const days = React.useMemo(
        () => dates.map(date => moment.tz(date, WAITLIST_DATE_FORMAT, timeZone)),
        [dates, timeZone]
    );

    const close = () => setIsOpen(false);

    const toDates = (selected: Array<Moment>) =>
        selected.map(date => date.format(WAITLIST_DATE_FORMAT)).sort();

    // Boarding shows the stay as check-in → check-out; the dates themselves are the nights.
    const period: Period | null =
        days.length > 0 ? { from: days[0], to: days[days.length - 1].clone().add(1, 'day') } : null;

    const periodChangeHandler = (value: Period | null) => {
        if (!value || !value.to.isAfter(value.from, 'day')) {
            return;
        }

        onChange(getWaitlistBoardingNights(value.from, value.to));
        close();
    };

    const singleChangeHandler = (value: Moment | null) => {
        if (!value) {
            return;
        }

        onChange(toDates([value]));
        close();
    };

    const sharedProps = {
        minDate,
        maxDate,
        timeZone,
        isDateDisabled,
        getDisabledDateTooltip,
        className: classes.calendar
    };

    return (
        <>
            <WaitlistPickerField
                ref={triggerRef}
                label={label}
                open={isOpen}
                value={
                    isBoarding || isDaycare
                        ? formatWaitlistDates(dates, isBoarding) ?? undefined
                        : days[0]?.format(WAITLIST_SINGLE_DATE_FORMAT)
                }
                placeholder={isBoarding || isDaycare ? 'Select dates' : 'Select date'}
                onClick={() => setIsOpen(true)}
            />

            <Popover
                open={isOpen}
                anchorEl={triggerRef.current}
                classes={{ paper: classes.popover }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClose={close}
            >
                {isBoarding && (
                    <InlineDatePicker
                        rangeMode
                        {...sharedProps}
                        value={period}
                        onChange={periodChangeHandler}
                    />
                )}

                {isDaycare && (
                    <InlineDatePicker
                        multipleMode
                        {...sharedProps}
                        value={days}
                        onChange={selected => onChange(toDates(selected))}
                    />
                )}

                {!isBoarding && !isDaycare && (
                    <InlineDatePicker
                        {...sharedProps}
                        value={days[0] ?? null}
                        onChange={singleChangeHandler}
                    />
                )}
            </Popover>
        </>
    );
};
